import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { api } from "../../../services/api";
import {
  BookOpen,
  Plus,
  Edit,
  Search,
  RefreshCw,
  BarChart3,
  GraduationCap,
  X,
  Loader2,
} from "lucide-react";

interface Treinamento {
  id: string;
  treinamento: string;
  descricao: string;
  criado_em: string;
  alterado_em: string;
}

interface TreinamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingTreinamento: Treinamento | null;
}

const TreinamentoModal: React.FC<TreinamentoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingTreinamento
}) => {
  const [formData, setFormData] = useState({
    treinamento: "",
    descricao: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingTreinamento) {
      setFormData({
        treinamento: editingTreinamento.treinamento,
        descricao: editingTreinamento.descricao
      });
    } else {
      setFormData({
        treinamento: "",
        descricao: ""
      });
    }
  }, [editingTreinamento, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (editingTreinamento) {
        await api.put(`/treinamentos/${editingTreinamento.id}`, formData);
        enqueueSnackbar('Treinamento atualizado com sucesso!', { variant: 'success' });
      } else {
        await api.post('/treinamentos', formData);
        enqueueSnackbar('Treinamento criado com sucesso!', { variant: 'success' });
      }

      onSave();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao salvar treinamento', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingTreinamento ? 'Editar Treinamento' : 'Novo Treinamento'}
            </CardTitle>
            <CardDescription>
              {editingTreinamento
                ? "Atualize as informações do treinamento"
                : "Crie um novo treinamento"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="treinamento">Nome do Treinamento *</Label>
            <InputText
              id="treinamento"
              name="treinamento"
              title=""
              value={formData.treinamento}
              onChange={(e) => setFormData(prev => ({ ...prev, treinamento: e.target.value }))}
              placeholder="Digite o nome do treinamento..."
              required
            />
          </div>

          <div>
            <Label htmlFor="descricao">Descrição *</Label>
            <textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
              placeholder="Digite a descrição do treinamento..."
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              required
            />
          </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingTreinamento ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  editingTreinamento ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export function Treinamentos() {
  const [treinamentos, setTreinamentos] = useState<Treinamento[]>([]);
  const [filteredTreinamentos, setFilteredTreinamentos] = useState<Treinamento[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTreinamento, setEditingTreinamento] = useState<Treinamento | null>(null);

  const loadTreinamentos = async () => {
    try {
      setLoading(true);
      const response = await api.get("/treinamentos");
      setTreinamentos(response.data);
      setFilteredTreinamentos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar treinamentos", { variant: "error" });
      setTreinamentos([]);
      setFilteredTreinamentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTreinamentos();
  }, []);

  // Filtrar treinamentos quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTreinamentos(treinamentos);
    } else {
      const filtered = treinamentos.filter(
        (treinamento) =>
          treinamento.treinamento.toLowerCase().includes(searchTerm.toLowerCase()) ||
          treinamento.descricao.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTreinamentos(filtered);
    }
  }, [searchTerm, treinamentos]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Busca já é feita em tempo real pelo useEffect
  };

  const openCreateModal = () => {
    setEditingTreinamento(null);
    setIsModalOpen(true);
  };

  const openEditModal = (treinamento: Treinamento) => {
    setEditingTreinamento(treinamento);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTreinamento(null);
  };

  const handleSaveSuccess = () => {
    closeModal();
    loadTreinamentos();
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Treinamentos
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os tipos de treinamentos do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Treinamentos
              </CardTitle>
              <CardDescription>
                Digite o nome do treinamento ou descrição para pesquisar ou deixe em branco para ver todos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Nome do Treinamento</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite o nome do treinamento..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={openCreateModal}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Treinamento
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredTreinamentos.length} treinamento(s) encontrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredTreinamentos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum treinamento encontrado
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTreinamentos.map((treinamento) => (
                    <div
                      key={treinamento.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{treinamento.treinamento}</h3>
                          <p className="text-sm text-muted-foreground mt-1 max-w-md">
                            {treinamento.descricao}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Criado em: {new Date(treinamento.criado_em).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(treinamento)}
                          className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Treinamentos</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {treinamentos.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Filtrados</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {filteredTreinamentos.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ações Rápidas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button
                  onClick={openCreateModal}
                  className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Treinamento
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    loadTreinamentos();
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <TreinamentoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveSuccess}
        editingTreinamento={editingTreinamento}
      />
    </div>
  );
}
